import { effect, inject, Injectable, untracked } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslationService } from '@core/translation.service';
import { companyId } from '@features/auth/store/auth.signals';
import { SearchOptions } from '@features/search/models/search-options';
import { SearchOptionsService } from '@features/search/services/search-options.service';
import { SearchTextResolverService } from '@features/search/services/search-text-resolver.service';
import { searchOptionsToRecord, searchOptionsToText } from '@features/search/utilities/criteria-formatters';
import { locale as criteriaLocale } from '@features/search/utilities/criteria-formatters/i18n/en';
import { filter } from 'rxjs';

import { CHATBOT_CONTEXT_IDS } from '../constants/chatbot-contexts';
import { ChatbotContextService } from './chatbot-context';

const SEARCH_PAGE_KEY = 'search';

@Injectable({
    providedIn: 'root',
})
export class ChatbotSearchContextService {
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly contextService = inject(ChatbotContextService);
    private readonly searchOptionsService = inject(SearchOptionsService);
    private readonly textResolver = inject(SearchTextResolverService);
    private readonly translationService = inject(TranslationService);

    private onSearchPage = false;

    constructor() {
        this.translationService.loadTranslations(criteriaLocale);

        effect(() => {
            const options = this.searchOptionsService.searchOptions();

            untracked(() => {
                if (this.onSearchPage) {
                    this.updateSearchContext(options);
                }
            });
        });
    }

    initialize(): void {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                const wasOnSearch = this.onSearchPage;

                this.onSearchPage = this.getPageKey() === SEARCH_PAGE_KEY;

                if (this.onSearchPage) {
                    this.updateSearchContext(this.searchOptionsService.searchOptions());
                } else if (wasOnSearch) {
                    this.contextService.remove(CHATBOT_CONTEXT_IDS.SEARCH_CRITERIA);
                }
            });
    }

    private updateSearchContext(options: SearchOptions): void {
        const t = (key: string): string => this.translationService.instant(key);
        const message = searchOptionsToText(options, this.textResolver, t, companyId());
        const data = searchOptionsToRecord(options, this.textResolver, t, companyId());

        // Publish the full structured criteria so the backend chatbot
        // `search` tool can hydrate its merge baseline from the page's
        // live state (not stale session state). Key is prefixed with `_`
        // so the backend context renderer skips it when building the LLM
        // prompt — the payload is for tool consumption only and does not
        // waste model tokens.
        const rawCriteria = this.searchOptionsService.buildSearchCriteria(options, null);

        data['_raw'] = JSON.stringify(rawCriteria);

        this.contextService.set(
            CHATBOT_CONTEXT_IDS.SEARCH_CRITERIA,
            'Search Criteria',
            message,
            data
        );
    }

    private getPageKey(): string {
        let route: ActivatedRoute = this.activatedRoute;

        while (route.firstChild) {
            route = route.firstChild;
        }

        const key = (route.snapshot.data['key'] as string | null) ?? '';

        return key.replace('prnyc-', '');
    }
}
